import { useEffect, useMemo, useState } from "react";
import { Download, FileText, Plus, Save, Trash2, X } from "lucide-react";
import { BestInterestTemplateControls, TaxRefundTemplateControls } from "./PlanLanguageTemplates";
import { appendNonstandardProvisionText, buildPdiLanguage, normalizePlanLanguageAmount } from "../../utils/chapter13PlanLanguage";

const clone = (value) => JSON.parse(JSON.stringify(value || {}));
const PERIODS = [["weekly", "Weekly"], ["biweekly", "Biweekly"], ["semi-monthly", "Semi-monthly"], ["monthly", "Monthly"]];
const SOURCES = [["direct", "Direct"], ["payroll", "Payroll deduction"]];
const MONEY_FIELDS = new Set(["amount", "installment_payment", "arrearage", "arrearage_payment", "secured_amount", "monthly_payment", "amount_of_lien", "other_liens", "exemption", "property_value", "impairment", "secured_claim"]);
const BOOLEAN_FIELDS = new Set(["principal_residence", "direct_pay"]);
const isChecked = (value) => value === true || ["true", "yes", "y", "1"].includes(String(value).toLowerCase());
const debtorNumber = (row) => Number(row?.debtor_number || String(row?.debtor_label || "").match(/debtor\s*([12])/i)?.[1] || 0);
const periodValue = (value) => ({ weekly: "weekly", biweekly: "biweekly", "bi-weekly": "biweekly", "semi-monthly": "semi-monthly", semimonthly: "semi-monthly", monthly: "monthly" }[String(value || "").trim().toLowerCase()] || value || "");
const sourceValue = (value) => ({ direct: "direct", payroll: "payroll", "payroll deduction": "payroll" }[String(value || "").trim().toLowerCase()] || value || "");
const dateInputValue = (value) => {
  const parts = String(value || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return parts ? `${parts[3]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}` : value || "";
};
const numberInputValue = (value) => value == null ? "" : String(value).replace(/[$,]/g, "").trim();
const futurePaymentEnd = (row, rows, debtors) => {
  const identity = (item) => debtorNumber(item)
    || Number(debtors.find((debtor) => String(debtor.debtor_id) === String(item.debtor_id))?.debtor_number)
    || `unselected-${item.debtor_id || ""}`;
  const start = dateInputValue(row.change_date);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return "Enter a start date to determine when this payment ends.";
  const next = rows.filter((item) => identity(item) === identity(row))
    .map((item) => dateInputValue(item.change_date))
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date) && date > start).sort()[0];
  if (!next) return "This payment applies for the remainder of the term.";
  const [year, month, day] = next.split("-");
  return `This payment applies until ${month}/${day}/${year}.`;
};
const unsecuredOptions = (section) => Array.isArray(section.unsecured_options)
  ? section.unsecured_options
  : (section.unsecured_treatment ? [["bic", "pdi"].includes(section.unsecured_treatment) ? "other" : section.unsecured_treatment] : []);

const normalizeCreditorSignatures = (value) => {
  const preserveBlankRows = Array.isArray(value);
  const entries = preserveBlankRows
    ? value
    : (typeof value === "string" ? value.split("\n") : []);
  const normalized = entries
    .map((entry) => {
      if (entry && typeof entry === "object") {
        return {
          name: entry.name || entry.attorney_name || entry.agent_name || "",
          creditor: entry.creditor || entry.creditor_name || "",
        };
      }
      return { name: String(entry || "").trim(), creditor: "" };
    });
  return preserveBlankRows
    ? normalized
    : normalized.filter((entry) => entry.name || entry.creditor);
};

const ROW_CONFIGS = {
  "2.3": [
    ["source", "Source"],
    ["amount", "Amount"],
    ["date", "Date"],
  ],
  "3.1": [
    ["creditor", "Creditor"],
    ["collateral", "Collateral"],
    ["principal_residence", "Principal residence"],
    ["installment_payment", "Installment"],
    ["direct_pay", "Direct pay"],
    ["arrearage", "Arrearage"],
    ["interest_rate", "Interest %"],
    ["arrearage_payment", "Arrearage payment"],
  ],
  "3.2": [
    ["creditor", "Creditor"],
    ["collateral", "Collateral"],
    ["secured_amount", "Secured amount"],
    ["interest_rate", "Interest %"],
    ["monthly_payment", "Monthly payment"],
    ["direct_pay", "Direct pay"],
  ],
  "3.3": [
    ["creditor", "Creditor"],
    ["collateral", "Collateral"],
    ["secured_amount", "Secured amount"],
    ["interest_rate", "Interest %"],
    ["monthly_payment", "Monthly payment"],
    ["direct_pay", "Direct pay"],
  ],
  "3.4": [
    ["creditor", "Creditor"],
    ["collateral", "Collateral description"],
    ["nature_of_lien", "Nature of lien"],
    ["lien_book", "Lien book"],
    ["lien_page", "Lien page"],
    ["lien_case_number", "Lien case number"],
    ["lien_county", "Lien county"],
    ["amount_of_lien", "Lien amount"],
    ["other_liens", "Other liens"],
    ["exemption", "Exemption"],
    ["property_value", "Property value"],
    ["impairment", "Impairment"],
    ["secured_claim", "Secured claim"],
    ["interest_rate", "Interest %"],
    ["monthly_payment", "Monthly payment"],
  ],
  "3.5": [
    ["creditor", "Creditor"],
    ["collateral", "Collateral description"],
  ],
  "6.1": [
    ["creditor", "Creditor"],
    ["description", "Contract or lease"],
    ["installment_payment", "Installment"],
    ["arrearage", "Arrearage"],
    ["arrearage_payment", "Arrearage payment"],
    ["direct_pay", "Direct pay"],
  ],
};

const statusStyle = (status) => {
  if (status === "generated") return "bg-green-100 text-green-800";
  if (status === "partial") return "bg-amber-100 text-amber-800";
  if (status === "failed") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-700";
};

async function responseError(response, fallback) {
  try {
    const body = await response.json();
    return body.detail || body.error || JSON.stringify(body);
  } catch (_) {
    return fallback;
  }
}

function RowEditor({ rows, fields, onChange }) {
  const updateRow = (rowIndex, key, value) => {
    onChange(rows.map((row, index) => (index === rowIndex ? { ...row, [key]: value } : row)));
  };
  const removeRow = (rowIndex) => onChange(rows.filter((_, index) => index !== rowIndex));

  return (
    <div className="space-y-3">
      {(rows || []).map((row, rowIndex) => (
        <div key={rowIndex} className="rounded border bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-600">Entry {rowIndex + 1}</span>
            <button
              type="button"
              className="text-xs font-medium text-red-600 hover:underline"
              onClick={() => removeRow(rowIndex)}
            >
              Remove
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {fields.map(([key, label]) => (
              <label key={key} className={key === "collateral" || key === "description" ? "md:col-span-2" : ""}>
                <span className="mb-1 block text-[11px] font-medium text-gray-600">{label}</span>
                <input
                  type={BOOLEAN_FIELDS.has(key) ? "checkbox" : key === "date" ? "date" : MONEY_FIELDS.has(key) || key === "interest_rate" ? "number" : "text"}
                  {...(BOOLEAN_FIELDS.has(key) ? { checked: isChecked(row?.[key]) } : { value: key === "date" ? dateInputValue(row?.[key]) : MONEY_FIELDS.has(key) || key === "interest_rate" ? numberInputValue(row?.[key]) : row?.[key] ?? "" })}
                  min={MONEY_FIELDS.has(key) || key === "interest_rate" ? "0" : undefined}
                  step={MONEY_FIELDS.has(key) || key === "interest_rate" ? "0.01" : undefined}
                  onChange={(event) => updateRow(rowIndex, key, BOOLEAN_FIELDS.has(key) ? event.target.checked : event.target.value)}
                  className={BOOLEAN_FIELDS.has(key) ? "h-5 w-5 rounded border" : "w-full rounded border px-2 py-1.5 text-sm"}
                />
              </label>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded border bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
        onClick={() => onChange([...(rows || []), Object.fromEntries(fields.map(([key]) => [key, BOOLEAN_FIELDS.has(key) ? false : ""]))])}
      >
        <Plus className="h-3.5 w-3.5" /> Add entry
      </button>
    </div>
  );
}

function PaymentEditor({ section, onChange, debtors }) {
  const payments = section.payments || [];
  const futureChanges = section.future_changes || [];
  const update = (collection, rows) => onChange({ ...section, [collection]: rows });

  const updateItem = (collection, index, key, value) => {
    const rows = section[collection] || [];
    update(collection, rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)));
  };

  const identity = (debtor) => debtor ? {
    debtor_number: debtor.debtor_number, debtor_id: debtor.debtor_id,
    debtor_label: debtor.label, debtor_name: debtor.name,
  } : {};
  const collectionEditor = (label, collection, rows) => (
    <div>
      <div className="mb-2 text-sm font-semibold">{label}</div>
      <div className="space-y-2">
        {rows.map((row, index) => {
          const selected = debtorNumber(row)
            ? debtors.find((debtor) => Number(debtor.debtor_number) === debtorNumber(row))
            : debtors.find((debtor) => row.debtor_id && String(debtor.debtor_id) === String(row.debtor_id));
          return (
          <fieldset key={index} className="grid grid-cols-2 gap-2 rounded border bg-white p-3 md:grid-cols-3">
            <legend className="px-1 text-xs font-semibold">{label} {index + 1}</legend>
            <label>
              <span className="mb-1 block text-xs font-medium">Payment debtor</span>
              <select value={selected?.debtor_number || ""} onChange={(event) => {
                const debtor = debtors.find((item) => String(item.debtor_number) === event.target.value);
                update(collection, rows.map((item, rowIndex) => rowIndex === index ? { ...item, ...identity(debtor) } : item));
              }} className="w-full rounded border px-2 py-1.5 text-sm">
                <option value="">Select a filing debtor</option>
                {debtors.map((debtor) => <option key={debtor.debtor_number} value={debtor.debtor_number}>{debtor.label} — {debtor.name}</option>)}
              </select>
            </label>
            <label>
              <span className="mb-1 block text-xs font-medium">Debtor name</span>
              <input readOnly value={selected?.name || ""} className="w-full rounded border bg-gray-50 px-2 py-1.5 text-sm" />
            </label>
            {!selected && <p className="col-span-2 text-xs text-amber-800 md:col-span-3">Choose a filing debtor for this payment. A nonfiling spouse cannot be selected.</p>}
            <label>
              <span className="mb-1 block text-xs font-medium">Amount</span>
              <input type="number" min="0" step="0.01" value={numberInputValue(row.amount)} onChange={(event) => updateItem(collection, index, "amount", event.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
            </label>
            <label>
              <span className="mb-1 block text-xs font-medium">Payment period</span>
              <select value={periodValue(row.period)} onChange={(event) => updateItem(collection, index, "period", event.target.value)} className="w-full rounded border px-2 py-1.5 text-sm">
                <option value="">Select period</option>
                {PERIODS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
              </select>
            </label>
            <label>
              <span className="mb-1 block text-xs font-medium">Payment source</span>
              <select value={sourceValue(row.source)} onChange={(event) => updateItem(collection, index, "source", event.target.value)} className="w-full rounded border px-2 py-1.5 text-sm">
                <option value="">Select source</option>
                {SOURCES.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
              </select>
            </label>
            <label>
              <span className="mb-1 block text-xs font-medium">{collection === "payments" ? "Number of months" : "Start date"}</span>
              <input type={collection === "payments" ? "number" : "date"} min={collection === "payments" ? "1" : undefined} step={collection === "payments" ? "1" : undefined} value={collection === "payments" ? row.months ?? "" : dateInputValue(row.change_date)} onChange={(event) => updateItem(collection, index, collection === "payments" ? "months" : "change_date", event.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
            </label>
            {collection === "future_changes" && <p className="col-span-2 text-xs text-gray-600 md:col-span-3">{futurePaymentEnd(row, rows, debtors)}</p>}
            <div className="col-span-2 flex justify-end md:col-span-3">
              <button
                type="button"
                onClick={() => update(collection, rows.filter((_, rowIndex) => rowIndex !== index))}
                className="text-xs font-medium text-red-600 hover:underline"
              >
                Remove
              </button>
            </div>
          </fieldset>
        );})}
      </div>
      <button
        type="button"
        onClick={() => update(collection, [...rows, { ...identity(debtors[0]), amount: "", period: "monthly", source: "direct", ...(collection === "payments" ? { months: "" } : { change_date: "" }) }])}
        disabled={!debtors.length}
        className="mt-2 inline-flex items-center gap-1 rounded border bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
      >
        <Plus className="h-3.5 w-3.5" /> Add {label.toLowerCase()}
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      {collectionEditor("Regular payment", "payments", payments)}
      {collectionEditor("Future payment change", "future_changes", futureChanges)}
      <label className="block">
        <span className="mb-1 block text-xs font-medium">Additional section language</span>
        <textarea
          rows={3}
          value={section.additional_language || ""}
          onChange={(event) => onChange({ ...section, additional_language: event.target.value })}
          className="w-full rounded border p-2 text-sm"
        />
      </label>
    </div>
  );
}

function UnsecuredEditor({ section, onChange, allowedJoint, onNonstandardProvision }) {
  const [templateError, setTemplateError] = useState("");
  const options = unsecuredOptions(section);
  const setOption = (option, checked) => {
    const next = checked ? [...new Set([...options, option])] : options.filter((item) => item !== option);
    onChange({ ...section, unsecured_options: next, unsecured_treatment: next.includes("other") ? (["bic", "pdi"].includes(section.unsecured_treatment) ? section.unsecured_treatment : "other") : next[0] || "",
      ...(option === "other" && checked && !section.unsecured_other_description ? { unsecured_other_description: section.treatment || "" } : {}) });
  };
  const applyBestInterest = (result, metadata) => {
    onChange({ ...section, unsecured_options: [...new Set([...options, "other"])], unsecured_treatment: "other",
      unsecured_other_description: result.description, other_amount: result.otherAmount,
      best_interest_template: metadata.kind, joint_best_interest_amount: metadata.jointAmount,
      non_joint_best_interest_amount: metadata.nonJointAmount });
    if (result.nonstandardProvision) onNonstandardProvision(result.nonstandardProvision);
  };
  return <div className="space-y-4">
    <fieldset className="space-y-2">
      <legend className="mb-2 text-sm font-semibold">5.1 Unsecured payment options</legend>
      <p className="text-xs text-gray-600">Select all applicable options. If more than one option is checked, the option providing the largest payment will be effective.</p>
      {[["full", "Full payment"], ["percent", "Percentage"], ["pro rata", "Pro rata on a funds-available basis"], ["other", "Other"]].map(([value, label]) => <label key={value} className="flex items-center gap-2">
        <input type="checkbox" checked={options.includes(value)} onChange={(event) => setOption(value, event.target.checked)} />{label}
      </label>)}
    </fieldset>
    {options.includes("percent") && <label className="block"><span className="mb-1 block text-xs font-medium">Unsecured percentage</span><input type="number" min="0" max="100" step="0.01" value={numberInputValue(section.unsecured_percentage)} onChange={(event) => onChange({ ...section, unsecured_percentage: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>}
    {options.includes("other") && <div className="space-y-3 rounded border bg-white p-3">
      <label className="block"><span className="mb-1 block text-xs font-medium">Other treatment</span><select value={["bic", "pdi"].includes(section.unsecured_treatment) ? section.unsecured_treatment : "other"} onChange={(event) => onChange({ ...section, unsecured_treatment: event.target.value })} className="w-full rounded border p-2 text-sm"><option value="other">Custom language / quick-fill template</option><option value="bic">Best interest amount</option><option value="pdi">Projected disposable income amount</option></select></label>
      {section.unsecured_treatment === "bic" && <label className="block"><span className="mb-1 block text-xs font-medium">Best interest amount</span><input type="number" min="0" step="0.01" value={numberInputValue(section.best_interest_amount)} onChange={(event) => onChange({ ...section, best_interest_amount: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>}
      {section.unsecured_treatment === "pdi" && <label className="block"><span className="mb-1 block text-xs font-medium">Projected disposable income amount</span><input type="number" min="0" step="0.01" value={numberInputValue(section.pdi_amount)} onChange={(event) => onChange({ ...section, pdi_amount: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>}
      <label className="block"><span className="mb-1 block text-xs font-medium">Other amount</span><input type="number" min="0" step="0.01" value={numberInputValue(section.other_amount)} onChange={(event) => onChange({ ...section, other_amount: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>
      <label className="block"><span className="mb-1 block text-xs font-medium">Other description</span><textarea rows={5} value={section.unsecured_other_description ?? section.treatment ?? ""} onChange={(event) => onChange({ ...section, unsecured_other_description: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>
      <BestInterestTemplateControls allowedJoint={allowedJoint} initialKind={section.best_interest_template} initialNonJointAmount={section.non_joint_best_interest_amount ?? section.other_amount ?? ""} initialJointAmount={section.joint_best_interest_amount ?? ""} onApply={applyBestInterest} />
      <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => {
        try {
          const amount = normalizePlanLanguageAmount(section.pdi_amount ?? section.other_amount ?? "", "PDI amount");
          const description = buildPdiLanguage(amount);
          onChange({ ...section, unsecured_treatment: "other", other_amount: amount, unsecured_other_description: description });
          setTemplateError("");
        } catch (failure) {
          setTemplateError(failure.message || "Enter a valid PDI amount.");
        }
      }}>Use PDI template</button>
      {templateError && <p role="alert" className="text-xs text-red-700">{templateError}</p>}
    </div>}
    {!section.unsecured_treatment && !Array.isArray(section.unsecured_options) && <div><label className="block"><span className="mb-1 block text-xs font-medium">Existing treatment language</span><textarea rows={5} value={section.treatment || ""} onChange={(event) => onChange({ ...section, treatment: event.target.value })} className="w-full rounded border p-2 text-sm" /></label><p className="mt-1 text-xs text-gray-600">This saved language is preserved. Choose an option above only to replace it with structured treatment.</p></div>}
    <label className="block"><span className="mb-1 block text-xs font-medium">Additional section language</span><textarea rows={3} value={section.additional_language || ""} onChange={(event) => onChange({ ...section, additional_language: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>
  </div>;
}

function TaxRefundEditor({ section, onChange }) {
  return <div className="space-y-3">
    <label className="block"><span className="mb-1 block text-xs font-medium">Tax refunds paid to trustee</span><select value={section.refund_payment || ""} onChange={(event) => onChange({ ...section, refund_payment: event.target.value, ...(event.target.value === "other" && !section.refund_payment_other_description ? { refund_payment_other_description: section.treatment || "" } : {}) })} className="w-full rounded border p-2 text-sm"><option value="">Select treatment</option><option value="all">All tax refunds</option><option value="none">No tax refunds</option><option value="other">Other</option></select></label>
    {section.refund_payment === "other" ? <>
      <label className="block"><span className="mb-1 block text-xs font-medium">Other refund treatment</span><textarea rows={4} value={section.refund_payment_other_description ?? section.treatment ?? ""} onChange={(event) => onChange({ ...section, refund_payment_other_description: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>
      <TaxRefundTemplateControls onApply={(language) => onChange({ ...section, refund_payment_other_description: language })} />
      <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => onChange({ ...section, refund_payment_other_description: "In excess of debtors' Earned Income and Child Tax Credits" })}>Use EITC/CTC excess</button>
    </> : !section.refund_payment && <label className="block"><span className="mb-1 block text-xs font-medium">Existing refund treatment language</span><textarea rows={4} value={section.treatment || ""} onChange={(event) => onChange({ ...section, treatment: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>}
    <label className="block"><span className="mb-1 block text-xs font-medium">Additional section language</span><textarea rows={3} value={section.additional_language || ""} onChange={(event) => onChange({ ...section, additional_language: event.target.value })} className="w-full rounded border p-2 text-sm" /></label>
  </div>;
}

function SectionEditor({ code, section, onChange, debtors, onNonstandardProvision }) {
  if (code === "2.1") return <PaymentEditor section={section} onChange={onChange} debtors={debtors} />;
  if (code === "2.2") return <TaxRefundEditor section={section} onChange={onChange} />;
  // A joint-creditor/TBE analysis can apply even when only one spouse files.
  if (code === "5.1") return <UnsecuredEditor section={section} onChange={onChange} allowedJoint onNonstandardProvision={onNonstandardProvision} />;
  if (ROW_CONFIGS[code]) {
    const rowKey = code === "2.3" ? "payments" : "rows";
    return (
      <div className="space-y-3">
        <RowEditor
          rows={section[rowKey] || []}
          fields={ROW_CONFIGS[code]}
          onChange={(rows) => onChange({ ...section, [rowKey]: rows })}
        />
        <label className="block">
          <span className="mb-1 block text-xs font-medium">Additional section language</span>
          <textarea
            rows={3}
            value={section.additional_language || ""}
            onChange={(event) => onChange({ ...section, additional_language: event.target.value })}
            className="w-full rounded border p-2 text-sm"
          />
        </label>
      </div>
    );
  }
  if (code === "4.2") {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label>
          <span className="mb-1 block text-xs font-medium">Total attorney fee</span>
          <input
            type="number" min="0" step="0.01"
            value={numberInputValue(section.total_fee)}
            onChange={(event) => onChange({ ...section, total_fee: event.target.value })}
            className="w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium">Paid before filing</span>
          <input
            type="number" min="0" step="0.01"
            value={numberInputValue(section.paid_prior)}
            onChange={(event) => onChange({ ...section, paid_prior: event.target.value })}
            className="w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>
        <label className="md:col-span-2">
          <span className="mb-1 block text-xs font-medium">Additional section language</span>
          <textarea
            rows={3}
            value={section.additional_language || ""}
            onChange={(event) => onChange({ ...section, additional_language: event.target.value })}
            className="w-full rounded border p-2 text-sm"
          />
        </label>
      </div>
    );
  }
  const primaryField = code === "8.1" ? "language" : "treatment";
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="mb-1 block text-xs font-medium">
          {code === "8.1" ? "Nonstandard provision language" : "Treatment language"}
        </span>
        <textarea
          rows={5}
          value={section[primaryField] || ""}
          onChange={(event) => onChange({ ...section, [primaryField]: event.target.value })}
          className="w-full rounded border p-2 text-sm"
        />
      </label>
      {code !== "8.1" && (
        <label className="block">
          <span className="mb-1 block text-xs font-medium">Additional section language</span>
          <textarea
            rows={3}
            value={section.additional_language || ""}
            onChange={(event) => onChange({ ...section, additional_language: event.target.value })}
            className="w-full rounded border p-2 text-sm"
          />
        </label>
      )}
    </div>
  );
}

export default function Chapter13ModificationModal({ caseId, token, apiUrl, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [defaultDate, setDefaultDate] = useState("");
  const [records, setRecords] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editorKey, setEditorKey] = useState(0);

  const baseUrl = `${apiUrl}/case/${caseId}/chapter-13-plan/modifications`;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [defaultsResponse, recordsResponse] = await Promise.all([
        fetch(`${baseUrl}/defaults/`, { headers }),
        fetch(`${baseUrl}/`, { headers }),
      ]);
      if (!defaultsResponse.ok) throw new Error(await responseError(defaultsResponse, "Unable to load modification defaults."));
      if (!recordsResponse.ok) throw new Error(await responseError(recordsResponse, "Unable to load saved modifications."));
      const defaultsPayload = await defaultsResponse.json();
      setCatalog(defaultsPayload.section_catalog || []);
      setDefaults(defaultsPayload.section_data || {});
      setDefaultDate(defaultsPayload.modification_date || "");
      setRecords(await recordsResponse.json());
    } catch (loadError) {
      setError(loadError.message || "Unable to load plan modifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseId]);

  if (!open) return null;

  const newDraft = () => {
    setEditorKey((current) => current + 1);
    setDraft({
      id: null,
      title: "Plan Modification",
      modification_date: defaultDate,
      selected_sections: [],
      section_data: clone(defaults),
      status: "draft",
    });
  };

  const editRecord = (record) => {
    setEditorKey((current) => current + 1);
    setDraft({ ...clone(record), section_data: clone(record.section_data) });
  };

  const setSection = (code, nextSection) => {
    setDraft((current) => ({
      ...current,
      section_data: { ...current.section_data, [code]: nextSection },
    }));
  };

  const addNonstandardProvision = (notice) => setDraft((current) => ({
    ...current,
    selected_sections: [...new Set([...(current.selected_sections || []), "8.1"])],
    section_data: {
      ...current.section_data,
      "8.1": {
        ...(current.section_data?.["8.1"] || {}),
        language: appendNonstandardProvisionText(current.section_data?.["8.1"]?.language || "", notice),
      },
    },
  }));

  const toggleSection = (code) => {
    setDraft((current) => {
      const selected = new Set(current.selected_sections || []);
      if (selected.has(code)) selected.delete(code);
      else selected.add(code);
      return {
        ...current,
        selected_sections: catalog.map((item) => item.code).filter((item) => selected.has(item)),
      };
    });
  };

  const saveDraft = async () => {
    setSaving(true);
    setError("");
    try {
      const url = draft.id ? `${baseUrl}/${draft.id}/` : `${baseUrl}/`;
      const response = await fetch(url, {
        method: draft.id ? "PATCH" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          title: draft.title,
          modification_date: draft.modification_date,
          selected_sections: draft.selected_sections || [],
          section_data: draft.section_data || {},
        }),
      });
      if (!response.ok) throw new Error(await responseError(response, "Unable to save the modification."));
      const saved = await response.json();
      setDraft(saved);
      setRecords((current) => [saved, ...current.filter((record) => record.id !== saved.id)]);
      return saved;
    } catch (saveError) {
      setError(saveError.message || "Unable to save the modification.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    if (!(draft.selected_sections || []).length) {
      setError("Select at least one plan section before generating the modification.");
      return;
    }
    const saved = await saveDraft();
    if (!saved) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${baseUrl}/${saved.id}/generate/`, {
        method: "POST",
        headers,
      });
      if (!response.ok) throw new Error(await responseError(response, "Unable to generate the modification."));
      const generated = await response.json();
      setDraft(generated);
      setRecords((current) => [generated, ...current.filter((record) => record.id !== generated.id)]);
      if (generated.status === "partial") setError(generated.error_message || "The Word document was created, but the PDF could not be created.");
    } catch (generateError) {
      setError(generateError.message || "Unable to generate the modification.");
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Delete ${record.title || "this plan modification"}?`)) return;
    setError("");
    const response = await fetch(`${baseUrl}/${record.id}/`, { method: "DELETE", headers });
    if (!response.ok && response.status !== 204) {
      setError(await responseError(response, "Unable to delete the modification."));
      return;
    }
    setRecords((current) => current.filter((item) => item.id !== record.id));
    if (draft?.id === record.id) setDraft(null);
  };

  const signatures = draft?.section_data?.signatures || {};
  const paymentDebtors = (defaults["2.1"]?.debtors || []).filter((debtor) => debtor.is_filing !== false);
  const creditorSignatures = normalizeCreditorSignatures(signatures.creditors);

  const setCreditorSignatures = (creditors) => setDraft((current) => ({
    ...current,
    section_data: {
      ...current.section_data,
      signatures: {
        ...(current.section_data?.signatures || {}),
        creditors,
      },
    },
  }));

  const updateCreditorSignature = (index, field, value) => {
    setCreditorSignatures(
      creditorSignatures.map((entry, entryIndex) => (
        entryIndex === index ? { ...entry, [field]: value } : entry
      )),
    );
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="plan-modifications-title" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="flex max-h-[96vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <header className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 id="plan-modifications-title" className="text-xl font-bold text-indigo-800">Chapter 13 Plan Modifications</h2>
            <p className="text-sm text-gray-600">Prepare and save modification drafts. This does not file or serve anything.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-2 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[300px_1fr]">
          <aside className="overflow-y-auto border-r bg-gray-50 p-4">
            <button
              type="button"
              onClick={newDraft}
              disabled={loading}
              className="mb-4 inline-flex w-full items-center justify-center gap-2 rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> New modification
            </button>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Saved modifications</div>
            {loading && <div className="text-sm text-gray-500">Loading…</div>}
            {!loading && records.length === 0 && <div className="text-sm text-gray-500">No saved modifications yet.</div>}
            <div className="space-y-2">
              {records.map((record) => (
                <div
                  key={record.id}
                  className={`rounded border p-3 ${draft?.id === record.id ? "border-indigo-500 bg-indigo-50" : "bg-white"}`}
                >
                  <button type="button" onClick={() => editRecord(record)} className="w-full text-left">
                    <div className="font-medium text-gray-900">{record.title}</div>
                    <div className="mt-1 text-xs text-gray-500">{record.modification_date}</div>
                    <span className={`mt-2 inline-block rounded px-2 py-0.5 text-[11px] font-medium ${statusStyle(record.status)}`}>
                      {record.status}
                    </span>
                  </button>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {record.docx_url && (
                      <a href={record.docx_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline">
                        <Download className="h-3.5 w-3.5" /> Word
                      </a>
                    )}
                    {record.pdf_url && (
                      <a href={record.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline">
                        <Download className="h-3.5 w-3.5" /> PDF
                      </a>
                    )}
                    <button type="button" onClick={() => deleteRecord(record)} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <main className="overflow-y-auto p-5">
            {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
            {!draft ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center text-gray-500">
                <FileText className="mb-3 h-10 w-10 text-gray-400" />
                <div className="font-medium">Choose a saved modification or create a new one.</div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px]">
                  <label>
                    <span className="mb-1 block text-xs font-medium">Draft name</span>
                    <input
                      value={draft.title || ""}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                      className="w-full rounded border px-3 py-2"
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium">Modification date</span>
                    <input
                      type="date"
                      value={draft.modification_date || ""}
                      onChange={(event) => setDraft({ ...draft, modification_date: event.target.value })}
                      className="w-full rounded border px-3 py-2"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-1 block text-xs font-medium">Opening notice language</span>
                  <textarea
                    rows={3}
                    value={draft.section_data?.notice_text || ""}
                    onChange={(event) => setDraft({
                      ...draft,
                      section_data: { ...draft.section_data, notice_text: event.target.value },
                    })}
                    className="w-full rounded border p-2 text-sm"
                  />
                </label>

                <div>
                  <div className="mb-2 text-sm font-semibold">Select the plan sections being modified</div>
                  <div className="space-y-3">
                    {catalog.map((item) => {
                      const selected = (draft.selected_sections || []).includes(item.code);
                      return (
                        <section key={item.code} className={`rounded border ${selected ? "border-indigo-300 bg-indigo-50/40" : "bg-white"}`}>
                          <label className="flex cursor-pointer items-start gap-3 p-3">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleSection(item.code)}
                              className="mt-1 h-4 w-4"
                            />
                            <span>
                              <span className="font-semibold">{item.code}</span>
                              <span className="ml-2 text-sm text-gray-700">{item.title}</span>
                            </span>
                          </label>
                          {selected && (
                            <div className="border-t p-3">
                              <SectionEditor
                                key={`${editorKey}-${item.code}`}
                                code={item.code}
                                section={draft.section_data?.[item.code] || {}}
                                onChange={(nextSection) => setSection(item.code, nextSection)}
                                debtors={paymentDebtors}
                                onNonstandardProvision={addNonstandardProvision}
                              />
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded border bg-gray-50 p-4">
                  <div className="mb-3 text-sm font-semibold">Signature lines</div>
                  <div className="space-y-4">
                    <label>
                      <span className="mb-1 block text-xs font-medium">Debtor's attorney</span>
                      <input
                        value={signatures.attorney_name || ""}
                        onChange={(event) => setDraft({
                          ...draft,
                          section_data: {
                            ...draft.section_data,
                            signatures: { ...signatures, attorney_name: event.target.value },
                          },
                        })}
                        className="w-full rounded border px-2 py-1.5 text-sm"
                      />
                    </label>
                    <div>
                      <div className="mb-2 text-xs font-medium">Creditor attorneys/agents</div>
                      <div className="space-y-2">
                        {creditorSignatures.map((entry, index) => (
                          <div key={index} className="grid grid-cols-1 gap-2 rounded border bg-white p-3 md:grid-cols-[1fr_1fr_auto]">
                            <label>
                              <span className="mb-1 block text-[11px] font-medium text-gray-600">Attorney/agent name</span>
                              <input
                                value={entry.name}
                                onChange={(event) => updateCreditorSignature(index, "name", event.target.value)}
                                className="w-full rounded border px-2 py-1.5 text-sm"
                              />
                            </label>
                            <label>
                              <span className="mb-1 block text-[11px] font-medium text-gray-600">Creditor represented</span>
                              <input
                                value={entry.creditor}
                                onChange={(event) => updateCreditorSignature(index, "creditor", event.target.value)}
                                className="w-full rounded border px-2 py-1.5 text-sm"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => setCreditorSignatures(creditorSignatures.filter((_, entryIndex) => entryIndex !== index))}
                              className="self-end rounded p-2 text-red-600 hover:bg-red-50"
                              aria-label={`Remove creditor attorney or agent ${index + 1}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCreditorSignatures([...creditorSignatures, { name: "", creditor: "" }])}
                        className="mt-2 inline-flex items-center gap-1 rounded border bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add creditor attorney/agent
                      </button>
                      <span className="mt-1 block text-[11px] text-gray-500">
                        The attorney/agent name may be left blank for an unsigned line. Each entry identifies the represented creditor below the line.
                      </span>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t bg-white py-3">
                  <button
                    type="button"
                    onClick={saveDraft}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded border px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" /> Save draft
                  </button>
                  <button
                    type="button"
                    onClick={generate}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    <FileText className="h-4 w-4" /> {draft.generated_at ? "Regenerate Word and PDF" : "Generate Word and PDF"}
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
